import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { API } from "@/App";
import { 
  Users, 
  GraduationCap, 
  TrendingUp, 
  Calendar,
  School,
  BarChart3,
  CheckCircle,
  AlertCircle,
  Clock,
  BookOpen,
  UserCheck,
  IndianRupee
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

export default function SchoolDashboard() {
  const { admin, school } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch school stats
      const statsResponse = await fetch(`${API}/school/stats`, {
        credentials: "include",
      });
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData.stats);
      }

      // Fetch analytics dashboard if permission
      if ((admin?.permissions || []).includes("view_analytics")) {
        const dashboardResponse = await fetch(`${API}/school/analytics/dashboard`, {
          credentials: "include",
        });
        
        if (dashboardResponse.ok) {
          const dashboardData = await dashboardResponse.json();
          setDashboard(dashboardData);
        }
      }
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "verified": return "bg-green-100 text-green-700";
      case "pending": return "bg-yellow-100 text-yellow-700";
      case "rejected": return "bg-red-100 text-red-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-1">Welcome, {admin?.name || "Admin"}!</h1>
            <p className="text-emerald-100 text-sm">{school?.name}</p>
            <div className="flex items-center gap-4 mt-3">
              <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                {school?.school_code}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm capitalize ${
                school?.status === "verified" ? "bg-green-400/30" : "bg-yellow-400/30"
              }`}>
                {school?.status || "Pending"}
              </span>
            </div>
          </div>
          <div className="hidden md:block">
            <School className="w-16 h-16 text-white/20" />
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats?.total_students || 0}</p>
                <p className="text-sm text-slate-500">Total Students</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats?.active_students || 0}</p>
                <p className="text-sm text-slate-500">Active Students</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats?.total_teachers || 0}</p>
                <p className="text-sm text-slate-500">Teachers</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <IndianRupee className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats?.billable_students || 0}</p>
                <p className="text-sm text-slate-500">Billable</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Grade Enrollments */}
      {stats?.grade_enrollments && stats.grade_enrollments.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-emerald-600" />
              Grade-wise Enrollment
            </CardTitle>
            <CardDescription>
              Students enrolled per grade (Minimum 50 required for school pricing)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats.grade_enrollments.map((grade) => (
                <div key={grade.grade} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-700">Grade {grade.grade}</span>
                    <span className={`font-medium ${grade.count >= 50 ? "text-green-600" : "text-amber-600"}`}>
                      {grade.count} students
                      {grade.count < 50 && (
                        <span className="text-xs text-slate-400 ml-2">
                          ({50 - grade.count} more needed)
                        </span>
                      )}
                    </span>
                  </div>
                  <Progress 
                    value={Math.min((grade.count / 50) * 100, 100)} 
                    className="h-2"
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Analytics Summary */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Learning Stats */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                Learning Statistics
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-slate-600">Total Questions Answered</span>
                <span className="font-bold text-slate-800">{dashboard.total_questions || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-slate-600">Average Accuracy</span>
                <span className="font-bold text-green-600">{dashboard.avg_accuracy?.toFixed(1) || 0}%</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-slate-600">Active Learners (7 days)</span>
                <span className="font-bold text-blue-600">{dashboard.active_7_days || 0}</span>
              </div>
            </CardContent>
          </Card>

          {/* Subscription Status */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                Subscription Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {school?.billing?.subscription ? (
                <>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">Plan</span>
                    <span className="font-bold text-slate-800 capitalize">
                      {school.billing.subscription.plan_tier?.replace("school_", "")}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">Cycle</span>
                    <span className="font-bold text-slate-800">
                      {school.billing.subscription.cycle?.replace("_", " ")}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">Status</span>
                    <span className={`font-bold capitalize ${
                      school.billing.subscription.status === "active" ? "text-green-600" : "text-amber-600"
                    }`}>
                      {school.billing.subscription.status}
                    </span>
                  </div>
                </>
              ) : (
                <div className="text-center py-6">
                  <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No active subscription</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Visit Billing to subscribe
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* School Info */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <School className="w-5 h-5 text-emerald-600" />
            School Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">School Name</p>
                <p className="font-medium text-slate-800">{school?.name}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Board</p>
                <p className="font-medium text-slate-800">{school?.board}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">School Type</p>
                <p className="font-medium text-slate-800 capitalize">{school?.school_type}</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Location</p>
                <p className="font-medium text-slate-800">{school?.city}, {school?.state}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Grades Offered</p>
                <p className="font-medium text-slate-800">
                  {school?.grades_offered?.join(", ") || "5, 6, 7, 8"}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Contact Email</p>
                <p className="font-medium text-slate-800">{school?.official_email}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
